import { useCallback, useRef } from "react";

import { create } from "@bufbuild/protobuf";

import { SourceSyncOperationStatus } from "@gx/ts-contracts/services/database/models/public_pb";
import {
  CompleteOperationUploadRequestSchema,
  CompletePartUploadRequestSchema,
} from "@gx/ts-contracts/services/uploader/api/uploader_pb";
import type {
  UploadOperationResource,
  UploadSyncResource,
} from "@gx/ts-contracts/services/uploader/resources/uploader_pb";

import type { FileUploadProgress } from "@/pages/sources/components/create/sources/types";

import {
  useCompleteOperationUploadMutation,
  useCompletePartUploadMutation,
} from "@/api/mutations/file-upload";

interface UseFileUploaderProps {
  files: File[];
  onSyncStart?: (sync: UploadSyncResource) => void;
  onInitFileProgress?: (progressMap: Map<string, FileUploadProgress>) => void;
  onFileStart?: (operationId: string) => void;
  onPartComplete?: (operationId: string, partsComplete: number, percentage: number) => void;
  onFileComplete?: (operationId: string) => void;
  onUploadComplete?: () => void;
}

interface UseFileUploaderResult {
  uploadSyncFiles: (sync: UploadSyncResource) => Promise<void>;
}

function mapFilesBySourcePath(files: File[]): Map<string, File> {
  return new Map(files.map((file) => [file.name, file]));
}

export function buildInitialProgressMap(
  sync: UploadSyncResource,
  localFilesBySourcePath: Map<string, File>,
): Map<string, FileUploadProgress> {
  const initialProgress = new Map<string, FileUploadProgress>();

  for (const operation of sync.operations) {
    const localFile = localFilesBySourcePath.get(operation.sourcePath);
    if (!localFile) {
      throw new Error(
        `Local file list no longer includes ${operation.sourcePath}. Please restart the upload.`,
      );
    }

    initialProgress.set(operation.id, {
      file: localFile,
      fileId: operation.id,
      status: operation.operationStatus,
      partsTotal: operation.partsTotal,
      partsComplete: operation.partsComplete,
      percentage:
        operation.partsTotal > 0
          ? Math.round((operation.partsComplete / operation.partsTotal) * 100)
          : 0,
    });
  }

  return initialProgress;
}

async function uploadPartToS3(presignedUrl: string, data: ArrayBuffer): Promise<string> {
  const response = await fetch(presignedUrl, {
    method: "PUT",
    body: data,
    headers: {
      "Content-Length": data.byteLength.toString(),
    },
  });

  if (!response.ok) {
    throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
  }

  const etag = response.headers.get("etag");
  if (!etag) {
    throw new Error("S3 did not return ETag header");
  }

  return etag.replace(/"/g, "");
}

export function useFileUploader({
  files,
  onSyncStart,
  onInitFileProgress,
  onFileStart,
  onPartComplete,
  onFileComplete,
  onUploadComplete,
}: UseFileUploaderProps): UseFileUploaderResult {
  const { mutateAsync: completePartUpload } = useCompletePartUploadMutation();
  const { mutateAsync: completeOperationUpload } = useCompleteOperationUploadMutation();

  const progressRef = useRef<Map<string, FileUploadProgress>>(new Map());

  const uploadFileToS3 = useCallback(
    async (
      syncId: string,
      operationResource: UploadOperationResource,
      localFile: File,
      initialPartsComplete: number,
    ) => {
      const operationId = operationResource.id;
      const partsTotal = Math.max(operationResource.partsTotal, 1);

      if (onFileStart) {
        onFileStart(operationId);
      }

      let partsComplete = initialPartsComplete;
      const sortedParts = [...operationResource.parts].sort((a, b) => a.partNumber - b.partNumber);

      for (const part of sortedParts) {
        if (part.partStatus === SourceSyncOperationStatus.COMPLETED) {
          continue;
        }

        if (!part.presignedUrl) {
          throw new Error(`Missing presigned URL for file ${operationResource.sourcePath}`);
        }

        const startByte = Number(part.startByte);
        const partSize = Number(part.partSize);

        const slice = localFile.slice(startByte, startByte + partSize);
        const buffer = await slice.arrayBuffer();

        const etag = await uploadPartToS3(part.presignedUrl, buffer);

        const completePartReq = create(CompletePartUploadRequestSchema, {
          syncId,
          operationId,
          partId: part.id,
          etag,
        });

        await completePartUpload(completePartReq);

        partsComplete += 1;
        const percentage = Math.round((partsComplete / partsTotal) * 100);

        const currentProgress = progressRef.current.get(operationId);

        if (!currentProgress) {
          throw new Error(`Missing progress state for file ${operationResource.sourcePath}`);
        }

        progressRef.current.set(operationId, {
          ...currentProgress,
          partsComplete,
          percentage,
        });

        if (onPartComplete) {
          onPartComplete(operationId, partsComplete, percentage);
        }
      }

      const completeOperationReq = create(CompleteOperationUploadRequestSchema, {
        syncId,
        operationId,
      });

      await completeOperationUpload(completeOperationReq);

      if (onFileComplete) {
        onFileComplete(operationId);
      }
    },
    [onFileStart, onPartComplete, onFileComplete, completePartUpload, completeOperationUpload],
  );

  const uploadSyncFiles = useCallback(
    async (sync: UploadSyncResource) => {
      if (files.length === 0) {
        throw new Error("No files to upload");
      }

      if (!sync.id) {
        throw new Error("Upload sync is missing an id");
      }

      if (sync.operations.length === 0) {
        throw new Error("Upload sync does not include any operations");
      }

      const localFilesBySourcePath = mapFilesBySourcePath(files);
      const initialProgress = buildInitialProgressMap(sync, localFilesBySourcePath);

      progressRef.current = initialProgress;

      if (onSyncStart) {
        onSyncStart(sync);
      }
      if (onInitFileProgress) {
        onInitFileProgress(initialProgress);
      }

      for (const operationResource of sync.operations) {
        const localFile = localFilesBySourcePath.get(operationResource.sourcePath);
        if (!localFile) {
          throw new Error(`Could not find local file for ${operationResource.sourcePath}`);
        }

        const initialPartsComplete =
          progressRef.current.get(operationResource.id)?.partsComplete ?? 0;
        await uploadFileToS3(sync.id, operationResource, localFile, initialPartsComplete);
      }

      if (onUploadComplete) {
        onUploadComplete();
      }
    },
    [files, onSyncStart, onInitFileProgress, onUploadComplete, uploadFileToS3],
  );

  return { uploadSyncFiles };
}
